/**
 * Phase 2: map kryspace render-manifest `control-bridge-personal-layer` section to hi-fi models.
 */

import type { KryspaceRenderManifest } from '../../../types/kryspaceRenderManifest';
import { fixUtf8Mojibake } from '../fixUtf8Mojibake';
import type { ControlBridgeQueueItem } from '../deriveViewModel';
import type { ResourceFilter, ScopeContext } from '../filters';
import { matchItemResource, passesFilter } from '../filters';
import { deriveGuidanceSummary, guidanceLayoutConstants } from './derivation';
import { adaptDriveHiFiModelForLiveShape } from './driveLiveShapeAdapter';
import type {
  DriveArtifactItem,
  DriveFilterHints,
  DriveDomainSection,
  DriveHiFiModel,
  DriveLaneMonitorStage,
  DrivePeriodRollup,
  DriveReportingPeriodRef,
  DriveSubdomainEdge,
  DriveSubdomainNode,
  GuidanceFilterHints,
  GuidanceHiFiModel,
  GuidanceLayoutHint,
  GuidanceTimelineWindow,
  GuidanceTreeNode,
} from './types';

export const PERSONAL_LAYER_MANIFEST_SECTION_ID = 'control-bridge-personal-layer';
export const PERSONAL_LAYER_MANIFEST_SCHEMA = 'control-bridge-personal-layer/v1';
export const DRIVE_OPERATIONAL_FLOW_SECTION_ID = 'drive-operational-flow';

export interface PersonalLayerManifestPayload {
  schemaVersion: string;
  source?: string;
  guidance?: GuidanceManifestPayload | null;
  drive?: DriveManifestPayload | null;
}

export interface GuidanceManifestPayload {
  sessionId: string;
  sessionLabel: string;
  objectiveLabel?: string;
  root: GuidanceTreeNode;
  weekCount?: number;
  weekWidthPx?: number;
  treeColumnWidthPx?: number;
  todayWeek?: number;
  timelineWindow?: GuidanceTimelineWindow;
  filterHints?: GuidanceFilterHints;
  layoutHint?: GuidanceLayoutHint;
}

export interface DriveManifestPayload {
  domainLabel: string;
  subdomains: DriveSubdomainNode[];
  edges: DriveSubdomainEdge[];
  /** Hierarchical domain sections from kryspace seq 60. */
  sections?: DriveDomainSection[];
  /** @deprecated Prefer flowArtifacts — kept for backward-compatible manifests. */
  artifacts: Array<
    DriveArtifactItem & {
      filterScope?: string;
      assignedResourceRef?: string;
    }
  >;
  /** Operational leaves that flow through lanes (excludes period rollup containers). */
  flowArtifacts?: Array<
    DriveArtifactItem & {
      filterScope?: string;
      assignedResourceRef?: string;
    }
  >;
  periodRollups?: DrivePeriodRollup[];
  activeReportingPeriod?: DriveReportingPeriodRef | null;
  laneMonitorStages?: DriveLaneMonitorStage[];
  orderFlowStages?: DriveHiFiModel['orderFlowStages'];
  summary: DriveHiFiModel['summary'];
  filterHints?: DriveFilterHints;
  stateColorTokens?: Record<string, string>;
  zoomLevels?: Array<'summary' | 'token' | 'detailed' | 'card'>;
  boundSessionKind?: 'operational' | 'development' | 'transitional';
  hasOperationalSession?: boolean;
  boundBridgeSessionRef?: string | null;
  emptyReason?: string | null;
}

export function findPersonalLayerSection(manifest: KryspaceRenderManifest) {
  return manifest.sections.find((s) => s.id === PERSONAL_LAYER_MANIFEST_SECTION_ID);
}

export function parseDriveOperationalFlowSection(
  manifest: KryspaceRenderManifest
): DriveManifestPayload | null {
  const section = manifest.sections.find((s) => s.id === DRIVE_OPERATIONAL_FLOW_SECTION_ID);
  const meta = section?.meta as Record<string, unknown> | undefined;
  const flow = meta?.flow;
  if (!flow || typeof flow !== 'object') return null;
  const payload = flow as DriveManifestPayload;
  if (!Array.isArray(payload.subdomains)) return null;
  const flowArtifacts = resolveFlowArtifacts(payload);
  if (!flowArtifacts.length) return null;
  return { ...payload, artifacts: flowArtifacts, flowArtifacts };
}

function resolveFlowArtifacts(payload: DriveManifestPayload): DriveManifestPayload['artifacts'] {
  if (payload.flowArtifacts?.length) return payload.flowArtifacts;
  return payload.artifacts ?? [];
}

/** Prefer personal-layer drive; fall back to drive-operational-flow when flow leaves missing. */
export function resolveDriveManifestPayload(
  manifest: KryspaceRenderManifest,
  personalLayer: PersonalLayerManifestPayload | null
): DriveManifestPayload | null {
  const fromPersonalLayer = personalLayer?.drive ?? null;
  const personalFlow = fromPersonalLayer ? resolveFlowArtifacts(fromPersonalLayer) : [];
  if (personalFlow.length) {
    return {
      ...fromPersonalLayer!,
      artifacts: personalFlow,
      flowArtifacts: personalFlow,
    };
  }
  const fromOperationalFlow = parseDriveOperationalFlowSection(manifest);
  if (fromOperationalFlow) {
    const flow = resolveFlowArtifacts(fromOperationalFlow);
    if (flow.length) {
      return { ...fromOperationalFlow, artifacts: flow, flowArtifacts: flow };
    }
  }
  return fromPersonalLayer ?? fromOperationalFlow;
}

export function parsePersonalLayerManifest(manifest: KryspaceRenderManifest): PersonalLayerManifestPayload | null {
  const section = findPersonalLayerSection(manifest);
  const meta = section?.meta as Record<string, unknown> | undefined;
  if (!meta || typeof meta !== 'object') return null;
  const schemaVersion = meta.schemaVersion;
  if (typeof schemaVersion !== 'string') return null;
  return {
    schemaVersion,
    source: typeof meta.source === 'string' ? meta.source : undefined,
    guidance: (meta.guidance as GuidanceManifestPayload | null) ?? null,
    drive: (meta.drive as DriveManifestPayload | null) ?? null,
  };
}

function queueLikeFromArtifact(
  art: DriveManifestPayload['artifacts'][number]
): ControlBridgeQueueItem {
  const scope = art.filterScope ?? art.subdomainId ?? art.lane ?? '';
  return {
    itemId: art.id,
    title: art.title,
    statusPhrase: art.status,
    domain: scope,
    filterScope: scope,
    assignedResourceRef: art.assignedResourceRef,
    assignedResourceLabel: art.owner,
    res: art.assignedResourceRef || art.owner,
  };
}

function queueLikeFromActivity(node: GuidanceTreeNode & { filterScope?: string; assignedResourceRef?: string; res?: string }): ControlBridgeQueueItem {
  return {
    itemId: node.id,
    title: node.label,
    statusPhrase: node.status,
    filterScope: node.filterScope,
    assignedResourceRef: node.assignedResourceRef,
    assignedResourceLabel: node.res,
    // Prefer ref for filter token; fall back to display res so person chips still match.
    res: node.assignedResourceRef || node.res,
  };
}

function applyMeFlags<T extends { mine?: boolean }>(
  row: T,
  isMine: boolean
): T {
  return { ...row, mine: isMine };
}

function sanitizeGuidanceField(value: string | undefined): string | undefined {
  if (value == null) return value;
  return fixUtf8Mojibake(value);
}

/** Repair mojibake / strip C0–C1 on Guidance tree copy (manifest path skips deriveViewModel). */
export function sanitizeGuidanceTreeNode(node: GuidanceTreeNode): GuidanceTreeNode {
  return {
    ...node,
    label: fixUtf8Mojibake(node.label),
    artifact: sanitizeGuidanceField(node.artifact),
    res: sanitizeGuidanceField(node.res),
    plankDescription: sanitizeGuidanceField(node.plankDescription),
    futureCondition: sanitizeGuidanceField(node.futureCondition),
    resourceAssignment: sanitizeGuidanceField(node.resourceAssignment),
    assumptionText: sanitizeGuidanceField(node.assumptionText),
    estimatedDuration: sanitizeGuidanceField(node.estimatedDuration),
    blockerTitle: sanitizeGuidanceField(node.blockerTitle),
    children: node.children?.map(sanitizeGuidanceTreeNode),
  };
}

function filterGuidanceTree(
  root: GuidanceTreeNode,
  scope: string,
  resource: ResourceFilter,
  companyWide: boolean,
  scopeContext: ScopeContext | undefined,
  userRef?: string,
  userLabel?: string
): GuidanceTreeNode {
  const cleanUserLabel = userLabel ? fixUtf8Mojibake(userLabel) : undefined;
  const isMineActivity = (node: GuidanceTreeNode & { filterScope?: string; assignedResourceRef?: string }) => {
    if (userRef && node.assignedResourceRef === userRef) return true;
    if (cleanUserLabel && node.res === cleanUserLabel) return true;
    return false;
  };
  const resourceNarrowed = Boolean(resource && resource !== 'all');
  const cleanRoot = sanitizeGuidanceTreeNode(root);

  const walk = (node: GuidanceTreeNode): GuidanceTreeNode | null => {
    if (node.type === 'activity') {
      const act = node as GuidanceTreeNode & { filterScope?: string; assignedResourceRef?: string; res?: string };
      const q = queueLikeFromActivity(act);
      if (!companyWide && !passesFilter(q, scope, resource, scopeContext)) return null;
      if (!passesFilter(q, companyWide ? '__all' : scope, resource, scopeContext)) return null;
      return applyMeFlags(node, isMineActivity(act));
    }
    const kids = (node.children ?? [])
      .map((c) => walk(c))
      .filter((c): c is GuidanceTreeNode => c != null);
    // Under All resources, keep empty session/waypoint/improvement shells (pre-decompose planks).
    // Under a person/type chip, keep ancestors only when they still have a matching leaf path.
    const keepWithoutChildren =
      !resourceNarrowed &&
      (node.type === 'session' || node.type === 'waypoint' || node.type === 'improvement');
    if (!keepWithoutChildren && kids.length === 0) {
      return null;
    }
    return { ...node, children: kids.length ? kids : undefined };
  };

  const filtered = walk(cleanRoot);
  return filtered ?? { ...cleanRoot, children: [] };
}

function mapGuidanceFromManifest(
  payload: GuidanceManifestPayload,
  scope: string,
  resource: ResourceFilter,
  companyWide: boolean,
  scopeContext: ScopeContext | undefined,
  userRef?: string,
  userLabel?: string
): GuidanceHiFiModel {
  const root = filterGuidanceTree(
    payload.root,
    scope,
    resource,
    companyWide,
    scopeContext,
    userRef,
    userLabel
  );
  const layout = guidanceLayoutConstants();
  const chartWidthPx = layout.weekCount * layout.weekWidthPx;
  const timelineWindow = payload.timelineWindow
    ? {
        ...payload.timelineWindow,
        weekCount: layout.weekCount,
        weekWidthPx: layout.weekWidthPx,
        chartWidthPx,
      }
    : undefined;
  return {
    sessionId: payload.sessionId,
    sessionLabel: fixUtf8Mojibake(payload.sessionLabel),
    objectiveLabel:
      payload.objectiveLabel != null ? fixUtf8Mojibake(payload.objectiveLabel) : undefined,
    root,
    summary: deriveGuidanceSummary(root),
    weekCount: layout.weekCount,
    weekWidthPx: layout.weekWidthPx,
    treeColumnWidthPx: layout.treeColumnWidthPx,
    todayWeek: payload.todayWeek,
    timelineWindow,
    filterHints: payload.filterHints,
    layoutHint: payload.layoutHint,
  };
}

function sanitizeDriveArtifact(
  art: DriveManifestPayload['artifacts'][number]
): DriveManifestPayload['artifacts'][number] {
  return {
    ...art,
    title: fixUtf8Mojibake(art.title),
    sub: art.sub != null ? fixUtf8Mojibake(art.sub) : art.sub,
    owner: fixUtf8Mojibake(art.owner),
    cause: art.cause != null ? fixUtf8Mojibake(art.cause) : art.cause,
  };
}

function sanitizeDriveSubdomain(node: DriveSubdomainNode): DriveSubdomainNode {
  return {
    ...node,
    name: fixUtf8Mojibake(node.name),
    grandchildren: node.grandchildren?.map((lane) => ({
      ...lane,
      name: fixUtf8Mojibake(lane.name),
    })),
  };
}

function sanitizeDriveSection(section: DriveDomainSection): DriveDomainSection {
  return { ...section, name: fixUtf8Mojibake(section.name) };
}

function mapDriveFromManifest(
  payload: DriveManifestPayload,
  _scope: string,
  resource: ResourceFilter,
  _companyWide: boolean,
  _scopeContext: ScopeContext | undefined,
  userRef?: string,
  userLabel?: string
): DriveHiFiModel {
  const cleanUserLabel = userLabel ? fixUtf8Mojibake(userLabel) : undefined;
  const sourceArtifacts = resolveFlowArtifacts(payload).map(sanitizeDriveArtifact);
  const artifacts = sourceArtifacts
    .filter((art) =>
      matchItemResource(
        {
          res: art.assignedResourceRef || art.owner,
          assignedResourceRef: art.assignedResourceRef,
          assignedResourceLabel: art.owner,
        },
        resource
      )
    )
    .map((art) =>
      applyMeFlags(
        art,
        Boolean(
          (userRef && art.assignedResourceRef === userRef) ||
            (cleanUserLabel && art.owner === cleanUserLabel)
        )
      )
    );

  const moved = artifacts.filter((a) => a.status === 'moved').length;
  const blocked = artifacts.filter((a) => a.status === 'blocked').length;
  const expected = artifacts.filter((a) => a.status === 'expected').length;

  return adaptDriveHiFiModelForLiveShape({
    domainLabel: fixUtf8Mojibake(payload.domainLabel),
    subdomains: payload.subdomains.map(sanitizeDriveSubdomain),
    edges: payload.edges,
    artifacts,
    sections: payload.sections?.map(sanitizeDriveSection),
    summary: {
      inFlow: artifacts.length,
      movedRecently: moved,
      blocked,
      expectedToMove: expected,
    },
    filterHints: payload.filterHints,
    stateColorTokens: payload.stateColorTokens,
    zoomLevels: payload.zoomLevels,
    ...(payload.activeReportingPeriod
      ? { activeReportingPeriod: payload.activeReportingPeriod }
      : {}),
    ...(payload.periodRollups?.length ? { periodRollups: payload.periodRollups } : {}),
    ...(payload.laneMonitorStages?.length
      ? { laneMonitorStages: payload.laneMonitorStages }
      : {}),
    ...(payload.orderFlowStages?.length
      ? {
          orderFlowStages: payload.orderFlowStages.map((stage) => ({
            sequence: stage.sequence,
            label: stage.label,
            wip: stage.wip,
            ...(stage.kind ? { kind: stage.kind } : {}),
          })),
        }
      : {}),
    ...(payload.boundSessionKind ? { boundSessionKind: payload.boundSessionKind } : {}),
    ...(payload.hasOperationalSession !== undefined
      ? { hasOperationalSession: payload.hasOperationalSession }
      : {}),
    ...(payload.boundBridgeSessionRef !== undefined
      ? { boundBridgeSessionRef: payload.boundBridgeSessionRef }
      : {}),
    ...(payload.emptyReason ? { emptyReason: payload.emptyReason } : {}),
  });
}

export interface MapPersonalLayerFromManifestInput {
  manifest: KryspaceRenderManifest;
  scope: string;
  resource: ResourceFilter;
  scopeContext?: ScopeContext;
  userRef?: string;
  userLabel?: string;
}

export function mapPersonalLayerFromManifest(
  input: MapPersonalLayerFromManifestInput
): { guidance: GuidanceHiFiModel | null; drive: DriveHiFiModel | null } | null {
  const parsed = parsePersonalLayerManifest(input.manifest);
  const drivePayload = resolveDriveManifestPayload(input.manifest, parsed);
  if (!parsed && !drivePayload) return null;

  const companyWide = !input.scope || input.scope === 'company';

  return {
    guidance: parsed?.guidance
      ? mapGuidanceFromManifest(
          parsed.guidance,
          input.scope,
          input.resource,
          companyWide,
          input.scopeContext,
          input.userRef,
          input.userLabel
        )
      : null,
    drive: drivePayload
      ? mapDriveFromManifest(
          drivePayload,
          input.scope,
          input.resource,
          companyWide,
          input.scopeContext,
          input.userRef,
          input.userLabel
        )
      : null,
  };
}
